/**
 * Dynamic trace attributes
 *
 * Faro's TracingInstrumentation is constructed once at app bootstrap (in main.tsx),
 * long before the Auth0 session or active entity are known. To tag spans with
 * runtime identity we keep a tiny module-level store here and read it from the
 * per-span `applyCustomAttributesOnSpan` hooks wired into the instrumentation.
 *
 * `useTraceUserAttributes` keeps this store in sync with the current user/entity.
 *
 */

export interface TraceUserAttributesInput {
  email?: string;
  uuid?: string;
  entityFriendlyId?: string;
}

// Plain string map — assignable to OTEL `Attributes` when passed to span.setAttributes().
let currentAttributes: Record<string, string> = {};

/**
 * Replace the attributes attached to subsequently-started spans. Undefined/empty
 * values are dropped so we never emit blank attributes (e.g. before login).
 */
export function setTraceUserAttributes(input: TraceUserAttributesInput): void {
  const next: Record<string, string> = {};
  if (input.email) next['user.email'] = input.email;
  if (input.uuid) next['user.id'] = input.uuid;
  if (input.entityFriendlyId) next['entity.friendly_id'] = input.entityFriendlyId;
  currentAttributes = next;
}

/**
 * Current trace attributes. Read imperatively by the span attribute hooks.
 */
export function getTraceUserAttributes(): Record<string, string> {
  return currentAttributes;
}
